import React, {
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import { Formik, Form, FieldArray, getIn } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { selectUser } from "../../redux/modules/userSlice";
import { EducationAPI } from "../api";
import useDraft from "../../hooks/useDraft";
import {
  applySectionDraftData,
  pickSectionDraftData,
} from "../../drafts/draftHelpers";
import {
  JOINEE_FORM_COLORS,
  JOINEE_FORM_FONT,
  LOCKED_QUALIFICATIONS,
  QUALIFICATION_OPTIONS,
  ADDABLE_QUALIFICATION_OPTIONS,
  emptyQualificationRow,
  defaultQualificationRows,
} from "../../config/joineeFormConstants";
import {
  FormSectionTitle,
  FormAddButton,
  FormTextField,
  FormSelectField,
  FormDateField,
  FormTwoCol,
} from "./JoineeFormFields";

import { ruleSchema, validateDraft, notifyFormInvalid } from "../../utils/inputRules";

var DRAFT_SECTION = "C";

function errMsg(formik, path) {
  var touched = getIn(formik.touched, path);
  var error = getIn(formik.errors, path);
  return touched && error ? error : "";
}

function isLockedIndex(index) {
  return index < LOCKED_QUALIFICATIONS.length;
}

function qualificationSchema() {
  return Yup.object({
    collegeName: ruleSchema("orgName", {
      label: "Name of College/School",
      required: true,
    }),
    qualification: Yup.string()
      .trim()
      .required("Qualification is required"),
    fromDate: Yup.mixed().nullable().required("From date is required"),
    toDate: Yup.mixed()
      .nullable()
      .required("To date is required")
      .test("after-from", "To date must be after From date", function (val) {
        var fromDate = this.parent.fromDate;
        if (!val || !fromDate) {
          return true;
        }
        return new Date(val).getTime() >= new Date(fromDate).getTime();
      }),
    specialisedIn: ruleSchema("text", {
      label: "Specialized In",
      required: true,
    }),
    university: ruleSchema("orgName", {
      label: "Board/University",
      required: true,
    }),
    registrationNo: ruleSchema("longCode", {
      label: "Registration Number",
      required: true,
    }),
    result: ruleSchema("result", { label: "Result", required: true }),
  });
}

function buildValidationSchema() {
  return Yup.object({
    qualifications: Yup.array()
      .of(qualificationSchema())
      .min(3, "At least 3 qualifications are required")
      .test(
        "unique-higher-qual",
        "Masters and PhD can only be added once",
        function (rows) {
          if (!rows || !rows.length) {
            return true;
          }
          var seen = {};
          for (var i = 0; i < rows.length; i++) {
            var q = rows[i] && rows[i].qualification;
            if (q !== "Masters" && q !== "PhD") {
              continue;
            }
            if (seen[q]) {
              return this.createError({
                path: "qualifications[" + i + "].qualification",
                message: q + " is already added",
              });
            }
            seen[q] = true;
          }
          return true;
        }
      ),
  });
}

function scrollToFirstEducationError() {
  setTimeout(function () {
    var target = document.querySelector(
      '.Mui-error, [aria-invalid="true"]'
    );
    if (target && typeof target.scrollIntoView === "function") {
      target.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, 50);
}

function optionsForRow(edu, allRows, locked) {
  if (locked) {
    return QUALIFICATION_OPTIONS;
  }
  var used = {};
  (allRows || []).forEach(function (row) {
    if (
      row &&
      (row.qualification === "Masters" || row.qualification === "PhD")
    ) {
      used[row.qualification] = true;
    }
  });
  return ADDABLE_QUALIFICATION_OPTIONS.filter(function (opt) {
    if (!opt.value) {
      return true;
    }
    if (edu && edu.qualification === opt.value) {
      return true;
    }
    if (opt.value === "Masters" || opt.value === "PhD") {
      return !used[opt.value];
    }
    return true;
  });
}

function setBusy(onBusyChange, value) {
  if (typeof onBusyChange === "function") {
    onBusyChange(value);
  }
}

function EducationHistory(props) {
  var formikRef = props.formikRef;
  var changeTab = props.changeTab;
  var handleButtonClick = props.handleButtonClick;
  var onBusyChange = props.onBusyChange;
  var hideInternalActions = Boolean(props.hideInternalActions);
  var readOnly = Boolean(props.readOnly);

  var reduxUser = useSelector(selectUser);
  var params = useParams();
  var id = params.id ? params.id : "";
  var user = reduxUser && reduxUser.user ? reduxUser.user : {};
  var isAdmin = Boolean(user.isAdmin);
  var userId = isAdmin ? id || props.id : user._id;

  var draftApi = useDraft("newJoinee", DRAFT_SECTION);
  var loadDraft = draftApi.loadDraft;
  var saveDraft = draftApi.saveDraft;
  var deleteDraft = draftApi.deleteDraft;

  var [initialValues, setInitialValues] = useState({
    qualifications: defaultQualificationRows(userId),
  });
  var [formKey, setFormKey] = useState(0);
  var [loading, setLoading] = useState(true);
  var formikInstanceRef = useRef(null);
  var deletedIdsRef = useRef([]);

  useEffect(
    function () {
      var isActive = true;

      function finish(rows) {
        if (!isActive) {
          return;
        }
        var next =
          rows && rows.length >= 3
            ? rows
            : defaultQualificationRows(userId);
        // Ensure locked defaults if API returned fewer/missing locked types.
        // Prefer API/source row matched by qualification (not by index).
        var ensured = defaultQualificationRows(userId).map(function (def) {
          var match = next.find(function (r) {
            return (
              r &&
              String(r.qualification || "").toLowerCase() ===
                String(def.qualification || "").toLowerCase()
            );
          });
          var merged = Object.assign({}, def, match || {}, {
            userId: userId,
            qualification: def.qualification,
          });
          // Keep non-empty college/school from source when merge blanks it
          if (
            match &&
            match.collegeName &&
            !String(merged.collegeName || "").trim()
          ) {
            merged.collegeName = match.collegeName;
          }
          return merged;
        });
        var extras = next.filter(function (r) {
          return (
            LOCKED_QUALIFICATIONS.indexOf(
              r && r.qualification ? r.qualification : ""
            ) === -1
          );
        });
        setInitialValues({
          qualifications: ensured.concat(extras),
        });
        setFormKey(function (k) {
          return k + 1;
        });
        setLoading(false);
      }

      setLoading(true);
      deletedIdsRef.current = [];

      function loadApi() {
        EducationAPI.getQualifications(userId)
          .then(function (res) {
            if (!isActive) {
              return;
            }
            var rows =
              res && res.data && res.data.length
                ? res.data.map(function (row) {
                    return Object.assign(
                      {},
                      emptyQualificationRow(userId),
                      row,
                      { userId: userId }
                    );
                  })
                : defaultQualificationRows(userId);
            finish(rows);
          })
          .catch(function (err) {
            console.log(err);
            if (isActive) {
              setLoading(false);
              toast.error("Unable to load education details");
            }
          });
      }

      if (!isAdmin) {
        Promise.all([
          loadDraft().catch(function () {
            return null;
          }),
          EducationAPI.getQualifications(userId).catch(function () {
            return null;
          }),
        ])
          .then(function (results) {
            if (!isActive) {
              return;
            }
            var draftData =
              results[0] && results[0].data ? results[0].data : results[0];
            var applied = applySectionDraftData(
              "newJoinee",
              DRAFT_SECTION,
              { qualifications: [] },
              draftData
            );
            var apiRows =
              results[1] && results[1].data && results[1].data.length
                ? results[1].data.map(function (row) {
                    return Object.assign(
                      {},
                      emptyQualificationRow(userId),
                      row,
                      { userId: userId }
                    );
                  })
                : [];

            var draftRows =
              applied &&
              Array.isArray(applied.qualifications) &&
              applied.qualifications.length
                ? applied.qualifications.map(function (row) {
                    return Object.assign(
                      {},
                      emptyQualificationRow(userId),
                      row,
                      { userId: userId }
                    );
                  })
                : [];

            // Prefer API values when draft left collegeName (or other fields) empty
            function mergePreferFilled(apiList, draftList) {
              if (!draftList.length) {
                return apiList;
              }
              if (!apiList.length) {
                return draftList;
              }
              var byId = {};
              var byQual = {};
              apiList.forEach(function (row) {
                if (row && row._id) {
                  byId[String(row._id)] = row;
                }
                if (row && row.qualification) {
                  byQual[String(row.qualification).toLowerCase()] = row;
                }
              });
              return draftList.map(function (draftRow) {
                var apiRow =
                  (draftRow &&
                    draftRow._id &&
                    byId[String(draftRow._id)]) ||
                  (draftRow &&
                    draftRow.qualification &&
                    byQual[String(draftRow.qualification).toLowerCase()]) ||
                  null;
                if (!apiRow) {
                  return draftRow;
                }
                var out = Object.assign({}, apiRow, draftRow);
                [
                  "collegeName",
                  "university",
                  "registrationNo",
                  "result",
                  "specialisedIn",
                ].forEach(function (key) {
                  if (!String(out[key] || "").trim() && apiRow[key]) {
                    out[key] = apiRow[key];
                  }
                });
                if (!out._id && apiRow._id) {
                  out._id = apiRow._id;
                }
                return out;
              });
            }

            var merged = mergePreferFilled(apiRows, draftRows);
            if (merged.length) {
              finish(merged);
              return;
            }
            loadApi();
          })
          .catch(function () {
            loadApi();
          });
      } else {
        loadApi();
      }

      return function () {
        isActive = false;
      };
    },
    [userId, isAdmin, loadDraft]
  );

  function handleSaveDraft(formik) {
    if (isAdmin) {
      return Promise.resolve();
    }
    return validateDraft(formik, buildValidationSchema()).then(function (ok) {
      return ok ? persistDraft(formik) : null;
    });
  }

  function persistDraft(formik) {
    var rows = formik.values.qualifications || [];
    var wrapped = pickSectionDraftData("newJoinee", DRAFT_SECTION, {
      qualifications: rows,
    });
    setBusy(onBusyChange, "draft");
    return saveDraft(wrapped)
      .then(function (saved) {
        setBusy(onBusyChange, false);
        if (saved && saved.skipped) {
          toast.info("Nothing to save as draft");
          return saved;
        }
        if (saved) {
          toast.success("Draft saved");
          return saved;
        }
        toast.error("Unable to save draft");
        return null;
      })
      .catch(function () {
        setBusy(onBusyChange, false);
        toast.error("Unable to save draft");
        return null;
      });
  }

  function handleSaveAndNext(values, formikHelpers) {
    var rows = values.qualifications || [];
    setBusy(onBusyChange, "next");
    if (typeof handleButtonClick === "function") {
      handleButtonClick("sectionC");
    }

    var deletes = (deletedIdsRef.current || []).map(function (delId) {
      return EducationAPI.deleteQualifications(delId).catch(function () {
        return null;
      });
    });

    Promise.all(deletes)
      .then(function () {
        return Promise.all(
          rows.map(function (row) {
            var payload = Object.assign({}, row, { userId: userId });
            if (payload._id) {
              return EducationAPI.updateQualifications(payload._id, payload);
            }
            return EducationAPI.createQualifications(payload);
          })
        );
      })
      .then(function () {
        deletedIdsRef.current = [];
        var after = isAdmin ? Promise.resolve(true) : deleteDraft();
        return after.then(function () {
          setBusy(onBusyChange, false);
          if (formikHelpers && formikHelpers.setSubmitting) {
            formikHelpers.setSubmitting(false);
          }
          toast.success("Education details saved");
          if (typeof changeTab === "function") {
            changeTab(3);
          }
        });
      })
      .catch(function (err) {
        console.log(err);
        setBusy(onBusyChange, false);
        if (formikHelpers && formikHelpers.setSubmitting) {
          formikHelpers.setSubmitting(false);
        }
        toast.error("Unable to save education details");
      });
  }

  useImperativeHandle(
    formikRef,
    function () {
      return {
        submitDraft: function () {
          var formik = formikInstanceRef.current;
          if (!formik || readOnly) {
            return Promise.resolve();
          }
          return handleSaveDraft(formik);
        },
        submitNext: function () {
          var formik = formikInstanceRef.current;
          if (!formik || readOnly) {
            return Promise.resolve();
          }
          return formik.validateForm().then(function (errors) {
            if (errors && Object.keys(errors).length) {
              notifyFormInvalid(errors);
              formik.setTouched(
                {
                  qualifications: (formik.values.qualifications || []).map(
                    function () {
                      return {
                        collegeName: true,
                        qualification: true,
                        fromDate: true,
                        toDate: true,
                        specialisedIn: true,
                        university: true,
                        registrationNo: true,
                        result: true,
                      };
                    }
                  ),
                },
                true
              );
              scrollToFirstEducationError();
              return;
            }
            return formik.submitForm();
          });
        },
        getFormik: function () {
          return formikInstanceRef.current;
        },
      };
    },
    [userId, isAdmin, onBusyChange, changeTab, handleButtonClick, readOnly]
  );

  if (loading) {
    return (
      <Box sx={{ py: 4, textAlign: "center" }}>
        <Typography
          sx={{
            fontFamily: JOINEE_FORM_FONT,
            fontSize: 14,
            fontWeight: 400,
            color: JOINEE_FORM_COLORS.placeholder,
          }}
        >
          Loading education details…
        </Typography>
      </Box>
    );
  }

  return (
    <Formik
      key={formKey}
      initialValues={initialValues}
      enableReinitialize
      validationSchema={buildValidationSchema()}
      onSubmit={handleSaveAndNext}
    >
      {function (formik) {
        formikInstanceRef.current = formik;
        var values = formik.values;

        function onFieldChange(e) {
          formik.handleChange(e);
        }

        return (
          <Form noValidate>
            <Box
              component="fieldset"
              disabled={readOnly}
              sx={{
                border: 0,
                m: 0,
                p: 0,
                minWidth: 0,
                pointerEvents: readOnly ? "none" : "auto",
              }}
            >
            <FieldArray name="qualifications">
              {function (arrayHelpers) {
                return (
                  <Box>
                    {(values.qualifications || []).map(function (edu, index) {
                      var base = "qualifications[" + index + "]";
                      var locked = isLockedIndex(index);
                      var options = optionsForRow(
                        edu,
                        values.qualifications,
                        locked
                      );
                      var rowKey =
                        (edu && edu._id) ||
                        "edu-" + index + "-" + (edu.qualification || "new");

                      return (
                        <Box key={rowKey} sx={{ mb: 3.5 }}>
                          <FormSectionTitle
                            action={
                              <Box sx={{ display: "flex", gap: 1 }}>
                                {index === 0 ? (
                                  <FormAddButton
                                    onClick={function () {
                                      arrayHelpers.push(
                                        emptyQualificationRow(userId, "")
                                      );
                                    }}
                                  >
                                    + Add Qualification
                                  </FormAddButton>
                                ) : null}
                                {!locked ? (
                                  <Button
                                    type="button"
                                    variant="outlined"
                                    onClick={function () {
                                      var row = values.qualifications[index];
                                      if (row && row._id) {
                                        deletedIdsRef.current =
                                          deletedIdsRef.current.concat([
                                            row._id,
                                          ]);
                                      }
                                      arrayHelpers.remove(index);
                                    }}
                                    sx={{
                                      fontFamily: JOINEE_FORM_FONT,
                                      fontSize: 14,
                                      fontWeight: 400,
                                      textTransform: "none",
                                      color: JOINEE_FORM_COLORS.error,
                                      borderColor: JOINEE_FORM_COLORS.border,
                                      borderRadius: "6px",
                                      minHeight: 32,
                                    }}
                                  >
                                    Remove
                                  </Button>
                                ) : null}
                              </Box>
                            }
                          >
                            Qualification {index + 1}
                          </FormSectionTitle>

                          <FormTwoCol>
                            <FormTextField
                              id={"edu-college-" + index}
                              name={base + ".collegeName"}
                              rule="orgName"
                              label="Name of College/School"
                              placeholder="Enter Name of College/School"
                              required
                              value={edu.collegeName}
                              onChange={onFieldChange}
                              onBlur={formik.handleBlur}
                              error={errMsg(formik, base + ".collegeName")}
                            />
                            <FormSelectField
                              id={"edu-qual-" + index}
                              name={base + ".qualification"}
                              label="Select the Qualification"
                              placeholder="Select the Qualification"
                              required
                              options={options}
                              value={edu.qualification}
                              onChange={onFieldChange}
                              onBlur={formik.handleBlur}
                              error={errMsg(formik, base + ".qualification")}
                              disabled={locked}
                            />
                          </FormTwoCol>

                          <FormTwoCol>
                            <FormDateField
                              id={"edu-from-" + index}
                              name={base + ".fromDate"}
                              label="From"
                              placeholder="Select From"
                              required
                              disableFuture
                              value={edu.fromDate}
                              onChange={function (iso) {
                                formik.setFieldValue(base + ".fromDate", iso);
                                if (
                                  edu.toDate &&
                                  iso &&
                                  new Date(edu.toDate).getTime() <
                                    new Date(iso).getTime()
                                ) {
                                  formik.setFieldValue(base + ".toDate", null);
                                }
                              }}
                              onBlur={formik.handleBlur}
                              error={errMsg(formik, base + ".fromDate")}
                            />
                            <FormDateField
                              id={"edu-to-" + index}
                              name={base + ".toDate"}
                              label="To"
                              placeholder="Select To"
                              required
                              minDate={edu.fromDate || undefined}
                              value={edu.toDate}
                              onChange={function (iso) {
                                formik.setFieldValue(base + ".toDate", iso);
                              }}
                              onBlur={formik.handleBlur}
                              error={errMsg(formik, base + ".toDate")}
                            />
                          </FormTwoCol>

                          <FormTwoCol>
                            <FormTextField
                              id={"edu-spec-" + index}
                              name={base + ".specialisedIn"}
                              rule="text"
                              label="Specialized In"
                              placeholder="Enter Specialized In"
                              required
                              value={edu.specialisedIn}
                              onChange={onFieldChange}
                              onBlur={formik.handleBlur}
                              error={errMsg(formik, base + ".specialisedIn")}
                            />
                            <FormTextField
                              id={"edu-uni-" + index}
                              name={base + ".university"}
                              rule="orgName"
                              label="Name of the Board/University"
                              placeholder="Enter Name of the Board/University"
                              required
                              value={edu.university}
                              onChange={onFieldChange}
                              onBlur={formik.handleBlur}
                              error={errMsg(formik, base + ".university")}
                            />
                          </FormTwoCol>

                          <FormTwoCol>
                            <FormTextField
                              id={"edu-reg-" + index}
                              name={base + ".registrationNo"}
                              rule="longCode"
                              label="Registration Number"
                              placeholder="Enter Registration Number"
                              required
                              value={edu.registrationNo}
                              onChange={onFieldChange}
                              onBlur={formik.handleBlur}
                              error={errMsg(formik, base + ".registrationNo")}
                            />
                            <FormTextField
                              id={"edu-result-" + index}
                              name={base + ".result"}
                              rule="result"
                              label="Result (%/CGPA)"
                              hint="Percentage (e.g. 80 or 80.5), CGPA (e.g. 8.5) or grade (e.g. A+, O, Pass, Distinction)"
                              placeholder="e.g. 80, 8.5 or A+"
                              required
                              value={edu.result}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              error={errMsg(formik, base + ".result")}
                            />
                          </FormTwoCol>
                        </Box>
                      );
                    })}
                  </Box>
                );
              }}
            </FieldArray>

            </Box>

            {!hideInternalActions ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 1.5,
                  mt: 2,
                }}
              >
                <Button
                  type="button"
                  variant="outlined"
                  onClick={function () {
                    handleSaveDraft(formik);
                  }}
                  sx={{
                    fontFamily: JOINEE_FORM_FONT,
                    textTransform: "none",
                    fontWeight: 400,
                  }}
                >
                  Save as Draft
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  sx={{
                    fontFamily: JOINEE_FORM_FONT,
                    textTransform: "none",
                    fontWeight: 400,
                    bgcolor: JOINEE_FORM_COLORS.primary,
                  }}
                >
                  Save and next
                </Button>
              </Box>
            ) : null}
          </Form>
        );
      }}
    </Formik>
  );
}

export default EducationHistory;
